import React, { forwardRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Root carousel component
export const Carousel = forwardRef(({ 
  opts, 
  setApi, 
  orientation = "horizontal", 
  children, 
  className = "", 
  ...props 
}, ref) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...opts,
    axis: orientation === "horizontal" ? "x" : "y",
  });

  React.useEffect(() => {
    if (emblaApi && setApi) {
      setApi(emblaApi);
    }
  }, [emblaApi, setApi]);

  return (
    <div 
      ref={ref} 
      className={`relative ${className}`} 
      {...props}
    >
      <div 
        ref={emblaRef} 
        className="overflow-hidden"
      >
        {children}
      </div>
    </div>
  );
});

Carousel.displayName = "Carousel";

// Carousel content wrapper
export const CarouselContent = forwardRef(({ 
  className = "", 
  ...props 
}, ref) => {
  return (
    <div 
      ref={ref} 
      className={`flex ${className}`} 
      {...props} 
    />
  );
});

CarouselContent.displayName = "CarouselContent";

// Individual carousel item
export const CarouselItem = forwardRef(({ 
  className = "", 
  ...props 
}, ref) => {
  return (
    <div 
      ref={ref} 
      className={`min-w-0 flex-shrink-0 flex-grow-0 ${className}`} 
      {...props} 
    />
  );
});

CarouselItem.displayName = "CarouselItem";

// Previous button
export const CarouselPrevious = forwardRef(({ 
  className = "", 
  variant = "outline", 
  size = "icon", 
  icon,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={`absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full flex items-center justify-center border border-white/20 bg-white/10 text-white hover:bg-white/20 ${className}`}
      {...props}
    >
      {icon || <ChevronLeft className="h-5 w-5" />}
      <span className="sr-only">Previous slide</span>
    </button>
  );
});

CarouselPrevious.displayName = "CarouselPrevious";

// Next button
export const CarouselNext = forwardRef(({ 
  className = "", 
  variant = "outline", 
  size = "icon", 
  icon,
  ...props 
}, ref) => {
  return (
    <button
      ref={ref}
      className={`absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full flex items-center justify-center border border-white/20 bg-white/10 text-white hover:bg-white/20 ${className}`}
      {...props}
    >
      {icon || <ChevronRight className="h-5 w-5" />}
      <span className="sr-only">Next slide</span>
    </button>
  );
});

CarouselNext.displayName = "CarouselNext";
